/**
 * @page BugReportInbox
 * @description Admin-Inbox fuer Bug-Reports (#943).
 * KPI-Bar oben + Tabs (Offen / Erledigt / Alle) + sortierbare Cards.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Bug, AlertOctagon, AlertTriangle, Sparkles, ExternalLink, RefreshCw, List, LayoutGrid } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// #985 5+1 Status — alte Keys werden auf neue Keys gemapped (Backwards-Compat fuer altdaten in der UI)
const STATUS_COLORS = {
  eingegangen: 'bg-[var(--bg-inset)] border-line text-content-secondary',
  in_arbeit: 'bg-blue-50 border-blue-200 text-blue-700',
  review: 'bg-amber-50 border-amber-200 text-amber-700',
  erledigt: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  nicht_reproduzierbar: 'bg-[var(--bg-inset)] border-line text-content-secondary italic',
};

const STATUS_FALLBACK_MAP = {
  triage: 'eingegangen',
  an_claude: 'in_arbeit',
  deployed: 'review',
  erneut_kaputt: 'in_arbeit',
  abgeschlossen: 'erledigt',
};

function statusKey(s) {
  const v = s || 'eingegangen';
  return STATUS_FALLBACK_MAP[v] || v;
}

const TABS = [
  { key: 'open', labelKey: 'bugInbox.tab_open' },
  { key: 'done', labelKey: 'bugInbox.tab_done' },
  { key: 'all', labelKey: 'bugInbox.tab_all' },
];

const KANBAN_COLUMNS = [
  'eingegangen',
  'in_arbeit',
  'review',
  'erledigt',
  'nicht_reproduzierbar',
];

export default function BugReportInbox() {
  const { t } = useLanguage();
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');
  const [sort, setSort] = useState('updated'); // updated | severity | iteration
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('bugInbox.view') || 'list'; } catch { return 'list'; }
  });
  function setViewPersist(v) {
    setView(v);
    try { localStorage.setItem('bugInbox.view', v); } catch { /* noop */ }
  }

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/bug-reports`, { credentials: 'include' });
      const j = await r.json();
      setBugs(j.bugs || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => {
    const k = { total: 0, kritisch: 0, wichtig: 0, kosmetisch: 0, offen: 0 };
    for (const b of bugs) {
      k.total += 1;
      const sk = statusKey(b.status);
      if (sk !== 'erledigt' && sk !== 'nicht_reproduzierbar') k.offen += 1;
      const s = String(b.schweregrad || '').toLowerCase();
      if (s.includes('kritisch')) k.kritisch += 1;
      else if (s.includes('wichtig')) k.wichtig += 1;
      else if (s.includes('kosmet')) k.kosmetisch += 1;
    }
    return k;
  }, [bugs]);

  const filtered = useMemo(() => {
    let list = bugs;
    if (tab === 'open') list = list.filter((b) => !['erledigt', 'nicht_reproduzierbar'].includes(statusKey(b.status)));
    else if (tab === 'done') list = list.filter((b) => ['erledigt', 'nicht_reproduzierbar'].includes(statusKey(b.status)));
    list = [...list];
    if (sort === 'severity') {
      const ord = { '🔴 Kritisch': 0, '🟡 Wichtig': 1, '🟢 Kosmetisch': 2 };
      list.sort((a, b) => (ord[a.schweregrad] ?? 9) - (ord[b.schweregrad] ?? 9));
    } else if (sort === 'iteration') {
      list.sort((a, b) => (b.iteration || 0) - (a.iteration || 0));
    } else {
      list.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    }
    return list;
  }, [bugs, tab, sort]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xl font-semibold text-content inline-flex items-center gap-2">
            <Bug size={20} className="text-[#338edc]" /> {t('bugInbox.title')}
          </div>
          <div className="text-xs text-content-secondary mt-0.5">{t('bugInbox.subtitle')}</div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-line bg-surface-card text-xs text-content-secondary hover:bg-surface-hover"
        >
          <RefreshCw size={12} /> {t('common.refresh')}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={<Bug size={16} className="text-[#338edc]" />} label={t('bugInbox.kpi_offen')} value={kpis.offen} />
        <KpiCard icon={<AlertOctagon size={16} className="text-rose-500" />} label={t('bugInbox.kpi_kritisch')} value={kpis.kritisch} />
        <KpiCard icon={<AlertTriangle size={16} className="text-amber-500" />} label={t('bugInbox.kpi_wichtig')} value={kpis.wichtig} />
        <KpiCard icon={<Sparkles size={16} className="text-emerald-500" />} label={t('bugInbox.kpi_kosmetisch')} value={kpis.kosmetisch} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line inline-flex">
            {TABS.map((tb) => (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`px-3 py-1 rounded-[8px] text-xs font-medium transition-colors ${
                  tab === tb.key ? 'bg-white text-content shadow-sm' : 'text-content-secondary hover:text-content'
                }`}
              >
                {t(tb.labelKey)}
              </button>
            ))}
          </div>
          <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line inline-flex">
            <button
              onClick={() => setViewPersist('list')}
              title={t('bugInbox.view_list')}
              className={`px-2.5 py-1 rounded-[8px] text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                view === 'list' ? 'bg-white text-content shadow-sm' : 'text-content-secondary hover:text-content'
              }`}
            >
              <List size={12} /> {t('bugInbox.view_list')}
            </button>
            <button
              onClick={() => setViewPersist('kanban')}
              title={t('bugInbox.view_kanban')}
              className={`px-2.5 py-1 rounded-[8px] text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                view === 'kanban' ? 'bg-white text-content shadow-sm' : 'text-content-secondary hover:text-content'
              }`}
            >
              <LayoutGrid size={12} /> {t('bugInbox.view_kanban')}
            </button>
          </div>
        </div>
        {view === 'list' && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-content-secondary">{t('bugInbox.sort_by')}</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-2 py-1 rounded-lg border border-line bg-surface-card text-xs text-content"
            >
              <option value="updated">{t('bugInbox.sort_updated')}</option>
              <option value="severity">{t('bugInbox.sort_severity')}</option>
              <option value="iteration">{t('bugInbox.sort_iteration')}</option>
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-content-secondary">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface-card border border-line rounded-card p-8 text-center text-sm text-content-secondary">
          {t('bugInbox.empty')}
        </div>
      ) : view === 'kanban' ? (
        <KanbanBoard bugs={filtered} t={t} />
      ) : (
        <div className="space-y-2">
          {filtered.map((b) => (
            <Link
              key={b.id}
              to={`/admin/bug-reports/${b.id}`}
              className="block bg-surface-card border border-line rounded-card p-4 hover:border-[#338edc]/40 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-[var(--text-tertiary)]">{b.br_nr || `BR-${b.id}`}</span>
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATUS_COLORS[statusKey(b.status)] || STATUS_COLORS.eingegangen}`}>
                      {t(`bugInbox.status.${statusKey(b.status)}`) || b.status}
                    </span>
                    {b.schweregrad && (
                      <span className="text-[10px] text-content-secondary">{b.schweregrad}</span>
                    )}
                    {b.modul && <span className="text-[10px] text-content-secondary">· {b.modul}</span>}
                    {b.iteration > 1 && (
                      <span className="text-[10px] text-rose-600">· {t('bugReport.iteration')} {b.iteration}</span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-content mt-1 truncate">{b.bug_titel}</div>
                  <div className="text-[11px] text-content-secondary mt-0.5">
                    {b.reporter_name} · {new Date(b.updated_at).toLocaleString('de-AT')}
                  </div>
                </div>
                {b.notion_url && (
                  <a
                    href={b.notion_url}
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                    rel="noreferrer"
                    title="Notion"
                    className="text-[var(--text-tertiary)] hover:text-content"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function KanbanBoard({ bugs, t }) {
  const grouped = useMemo(() => {
    const map = Object.fromEntries(KANBAN_COLUMNS.map((k) => [k, []]));
    for (const b of bugs) {
      const k = statusKey(b.status);
      if (map[k]) map[k].push(b);
      else map.eingegangen.push(b);
    }
    for (const k of KANBAN_COLUMNS) {
      map[k].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    }
    return map;
  }, [bugs]);

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {KANBAN_COLUMNS.map((col) => (
        <div key={col} className="flex-shrink-0 w-72">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATUS_COLORS[col]}`}>
              {t(`bugInbox.status.${col}`)}
            </span>
            <span className="text-[10px] text-content-secondary">{grouped[col].length}</span>
          </div>
          <div className="bg-[var(--bg-inset,#F8F9FC)] border border-line rounded-card p-2 min-h-[120px] space-y-2">
            {grouped[col].length === 0 ? (
              <div className="text-[11px] text-content-secondary text-center py-6">—</div>
            ) : (
              grouped[col].map((b) => (
                <Link
                  key={b.id}
                  to={`/admin/bug-reports/${b.id}`}
                  className="block bg-surface-card border border-line rounded-card p-2.5 hover:border-[#338edc]/40 transition-colors"
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-[10px] text-[var(--text-tertiary)]">{b.br_nr || `BR-${b.id}`}</span>
                    {b.iteration > 1 && (
                      <span className="text-[10px] text-rose-600">· {t('bugReport.iteration')} {b.iteration}</span>
                    )}
                  </div>
                  <div className="text-xs font-medium text-content mt-1 line-clamp-2">{b.bug_titel}</div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {b.schweregrad && (
                      <span className="text-[10px] text-content-secondary">{b.schweregrad}</span>
                    )}
                    {b.modul && <span className="text-[10px] text-content-secondary">· {b.modul}</span>}
                  </div>
                  <div className="text-[10px] text-content-secondary mt-1 truncate">
                    {b.reporter_name}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function KpiCard({ icon, label, value }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-3">
      <div className="flex items-center gap-2 mb-1">{icon}<div className="text-xs text-content-secondary">{label}</div></div>
      <div className="text-2xl font-semibold text-content">{value}</div>
    </div>
  );
}
